import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPaymentMethodDetails, fetchChallengeUrl } from './api';

const TERMINAL_AUTHENTICATION_STATUSES = [
  'authenticated',
  'failed',
  'declined',
] as const;

const isTerminalAuthenticationStatus = (
  status: PMethod['authentication_status']
) =>
  status !== null &&
  status !== undefined &&
  TERMINAL_AUTHENTICATION_STATUSES.includes(
    status as (typeof TERMINAL_AUTHENTICATION_STATUSES)[number]
  );

type UsePaymentMethodPollingArgs = {
  paymentMethodId: string | null;
  enabled?: boolean;
};

type UsePaymentMethodPollingReturn = {
  paymentMethod: PMethod | null;
  isPolling: boolean;
  error: string | null;
  stop: () => void;
};

const POLLING_INTERVAL = 2000;
const MAX_POLLING_DURATION = 5 * 60 * 1000;

export const usePaymentMethodPolling = ({
  paymentMethodId,
  enabled = true,
}: UsePaymentMethodPollingArgs): UsePaymentMethodPollingReturn => {
  const [paymentMethod, setPaymentMethod] = useState<PMethod | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isPollingRef = useRef(false);
  const lastMethodRef = useRef<PMethod | null>(null);
  const challengePopupRef = useRef<Window | null>(null);
  const challengeOpenedRef = useRef(false);

  const closePopup = useCallback(() => {
    if (challengePopupRef.current && !challengePopupRef.current.closed) {
      challengePopupRef.current.close();
    }
    challengePopupRef.current = null;
  }, []);

  const stop = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    closePopup();
    setIsPolling(false);
    isPollingRef.current = false;
    startTimeRef.current = null;
    challengeOpenedRef.current = false;
  }, [closePopup]);

  useEffect(() => {
    if (!enabled || !paymentMethodId) {
      stop();
      return;
    }

    const openChallengePopup = async (pmId: string, signal?: AbortSignal) => {
      if (challengeOpenedRef.current) return;
      challengeOpenedRef.current = true;

      const url = await fetchChallengeUrl(pmId, signal);
      if (!url) return;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      challengePopupRef.current = window.open(
        url,
        'bank-auth-challenge',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );
    };

    const startPolling = async () => {
      isPollingRef.current = true;
      setIsPolling(true);
      setError(null);
      startTimeRef.current = Date.now();
      challengeOpenedRef.current = false;
      abortControllerRef.current = new AbortController();

      const pollPaymentMethod = async () => {
        try {
          if (
            startTimeRef.current &&
            Date.now() - startTimeRef.current > MAX_POLLING_DURATION
          ) {
            stop();
            setError('Authentication timeout. Please try again.');
            return;
          }

          const method = await fetchPaymentMethodDetails(
            paymentMethodId,
            abortControllerRef.current?.signal
          );

          lastMethodRef.current = method;
          setPaymentMethod(method);

          if (method.authentication_status === 'challenged') {
            openChallengePopup(
              paymentMethodId,
              abortControllerRef.current?.signal
            );
          }

          if (isTerminalAuthenticationStatus(method.authentication_status)) {
            stop();
          }
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          // eslint-disable-next-line no-console
          console.error('Error polling payment method:', err);
          setError('Failed to check authentication status');
        }
      };

      // Initial fetch
      await pollPaymentMethod();

      // Continue polling if still challenged and not stopped
      if (
        isPollingRef.current &&
        !isTerminalAuthenticationStatus(
          lastMethodRef.current?.authentication_status
        )
      ) {
        pollingIntervalRef.current = setInterval(
          pollPaymentMethod,
          POLLING_INTERVAL
        );
      }
    };

    startPolling();

    // eslint-disable-next-line
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, paymentMethodId]);

  return {
    paymentMethod,
    isPolling,
    error,
    stop,
  };
};
